import i18n from 'i18next';
import { useSelector } from 'react-redux';
import {
  ConciliationHeader,
  StoreExtraInfoParameters,
  RenderNestedComponent,
  OnClickTo,
  conciliationHeaderKeys,
  ChildMap,
  conciliationHeaderKeys as K,
  FilterByColumn,
} from '../../types';

import {
  reportConciliationHeaderFilterByStateSelector,
  reportFilterCountryCodeSelector,
} from '../../state';

import StatusCell from '../StatusCell';
import SuffixCell from '../SuffixCell';
import { DataGridHandler } from '../DataGridFlexSales';
import { country } from '../DataGridFlexSales/commonColumnsProps';

export interface ConciliationsDataGridContext {
  t: (key: string | string[]) => string;
  theme?: any;
  country: string;
  storeExtraInfo: StoreExtraInfoParameters[];
  onClickTo?: OnClickTo;
  renderNestedComponent?: RenderNestedComponent;
  childMap?: ChildMap;
  filterByColumn?: FilterByColumn;
  conciliationMethod?: 'G' | 'D';
  federativeEntityByStore: Map<string, string>; // ← nuevo
}

const AFFIX_$ = '$';
const AFFIX_GSC = ' Gcs';
/* const ROW_KEY_GCS = 'GCs:';
const ROW_KEY_TOTAL = 'Total Amount($):'; anterior */

const ROW_KEY_GCS = 'conciliationHeaders.labels.gcs'; // nuevo
const ROW_KEY_TOTAL = 'conciliationHeaders.labels.totalAmt'; // nuevo

const DETAIL_ROWS = [ROW_KEY_GCS, ROW_KEY_TOTAL] as const;
const LABEL_COLUMN_ACCESSOR = 'status';

const CHILD_MAP: ChildMap = {
  labelColumnAccessor: LABEL_COLUMN_ACCESSOR,
  childRows: DETAIL_ROWS,
  map: {
    flexVentas: [
      { rowKey: ROW_KEY_GCS, accessor: K.gcs },
      { rowKey: ROW_KEY_TOTAL, accessor: K.totalAmt, prefix: AFFIX_$ },
    ],
    fiscal: [
      { rowKey: ROW_KEY_GCS, accessor: K.gcsFs },
      { rowKey: ROW_KEY_TOTAL, accessor: K.totalAmtFs, prefix: AFFIX_$ },
    ],
    cancelledBySubstitution: [
      { rowKey: ROW_KEY_GCS, accessor: K.qtyCancSust },
      { rowKey: ROW_KEY_TOTAL, accessor: K.totalAmtCancSust, prefix: AFFIX_$ },
    ],
    notConciliated: [
      { rowKey: ROW_KEY_GCS, accessor: K.gcsDif },
      { rowKey: ROW_KEY_TOTAL, accessor: K.totalAmtDif, prefix: AFFIX_$ },
    ],
    registeredOnlyInFlexVentas: [
      { rowKey: ROW_KEY_GCS, accessor: K.gcsFv, suffix: AFFIX_GSC },
      { rowKey: ROW_KEY_TOTAL, accessor: K.totalAmtFv, prefix: AFFIX_$ },
    ],
    registeredOnlyInFiscal: [
      { rowKey: ROW_KEY_GCS, accessor: K.gcsFiscal, suffix: AFFIX_GSC },
      { rowKey: ROW_KEY_TOTAL, accessor: K.totalAmtFiscal, prefix: AFFIX_$ },
    ],
  },
};

const onClickTo = {
  pathname: 'protectedRoutes.conciliationDetail.path',
  paramRows: [
    conciliationHeaderKeys.conciliationHeaderId,
    conciliationHeaderKeys.accountingDate,
    conciliationHeaderKeys.country,
    conciliationHeaderKeys.store,
  ],
};

const filterByColumn: FilterByColumn = {
  accessor: LABEL_COLUMN_ACCESSOR,
  selector: reportConciliationHeaderFilterByStateSelector,
};

const initialContext: ConciliationsDataGridContext = {
  t: () => '',
  country: '',
  storeExtraInfo: [],
  onClickTo,
  childMap: CHILD_MAP,
  filterByColumn,
  federativeEntityByStore: new Map(), // ← nuevo
};

export default (locale: Locale, countryCode?: string) => {
  const handler = new DataGridHandler<ConciliationHeader, ConciliationsDataGridContext>(
    initialContext,
    locale
  );

  handler.addColumn({
    accessor: 'store',
    type: 'string',
    title: ({ t, country }) => t([`table.columns.${country}.store`, 'table.columns.store']),
    description: ({ t }) => t('table.description.store'),
  });

  // nuevo
  handler.addColumn({
    name: 'federativeEntity',
    type: 'string',
    title: ({ t }) => t('table.columns.federativeEntity'),
    description: ({ t }) => t('table.description.federativeEntity'),
    valueGetter: (row, { federativeEntityByStore }) =>
      federativeEntityByStore.get(row.store) ?? '-',
  });
  // fin nuevo

  handler.addColumn({
    accessor: 'accountingDate',
    type: 'date',
    title: ({ t }) => t('table.columns.accountingDate'),
    description: ({ t }) => t('table.description.accountingDate'),
  });

  handler.addColumn({
    accessor: 'status',
    type: 'string',
    title: ({ t }) => t('table.columns.status'),
    description: ({ t }) => t('table.description.status'),
    formatter: value => {
      const raw = String(value ?? '');
      return i18n.t(`conciliation-header.state-types.${raw}`, { defaultValue: raw || '-' });
    },
    // width: { px: 200, pt: 400 },
    renderCell: (_value, row) => (
      <StatusCell
        status={row.status}
        tPath={`conciliation-header.state-types.${row.status}`}
        sx={{ textAlign: 'center' }}
      />
    ),
  });

  handler.addColumn({
    accessor: 'flexVentas',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.flexVentas'),
    // description: ({ t }) => t('conciliationHeaders.description.flexVentas'),
    width: { px: 120, pt: 100 },
    renderCell: () => null,
  });

  handler.addColumn({
    accessor: 'fiscal',
    type: 'number',
    width: { px: 120, pt: 100 },
    title: ({ t }) => t('conciliationHeaders.columns.fiscal'),
    // description: ({ t }) => t('conciliationHeaders.description.fiscal'),
    renderCell: () => null,
  });

  handler.addColumn({
    accessor: 'cancelledBySubstitution',
    type: 'number',
    title: ({ t }) =>
      // usa la key que prefieras en tu i18n
      t('conciliationHeaders.columns.cancelledBySubstitution') || 'Canceladas por sustitución',
    available: ctx => {
      console.log('available ctx.country', ctx.country);
      return ctx.conciliationMethod !== 'G';
    },
    renderCell: (_value, row, isExpanded) =>
      isExpanded ? null : (
        <>
          <SuffixCell content={row.qtyCancSust} suffix=" Gcs / $ " />
          <SuffixCell content={row.totalAmtCancSust} />
        </>
      ),
  });

  handler.addColumn({
    accessor: 'notConciliated',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.notConciliated'),
    // description: ({ t }) => t('conciliationHeaders.description.notConciliated'),
    available: ctx => ctx.conciliationMethod !== 'G',
    renderCell: (_value, row, _ctx, isExpanded) =>
      isExpanded ? null : (
        <>
          <SuffixCell content={row.gcsDif} suffix=" Gcs / $ " />
          <SuffixCell content={row.totalAmtDif} />
        </>
      ),
  });

  handler.addColumn({
    accessor: 'registeredOnlyInFlexVentas',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.registeredOnlyInFlexVentas'),
    available: ctx => ctx.conciliationMethod !== 'G',
    // description: ({ t }) => t('conciliationHeaders.description.registeredOnlyInFlexVentas'),
    renderCell: (_value, row, isExpanded) =>
      isExpanded ? null : <SuffixCell content={row.gcsFv} suffix=" Gcs" />,
  });

  handler.addColumn({
    accessor: 'registeredOnlyInFiscal',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.registeredOnlyInFiscal'),
    available: ctx => ctx.conciliationMethod !== 'G',
    // description: ({ t }) => t('conciliationHeaders.description.registeredOnlyInFiscal'),
    renderCell: (_value, row, isExpanded) =>
      isExpanded ? null : <SuffixCell content={row.gcsFiscal} suffix=" Gcs" />,
  });

  const PA_COLUMNS = [
    'store',
    'federativeEntity', // ← nuevo
    'accountingDate',
    'status',
    'flexVentas__gcs',
    'flexVentas__total',
    'fiscal__gcs',
    'fiscal__total',
  ];
  // Columnas para exportar
  // ----------flexVentas
  handler.addColumn({
    name: 'flexVentas__gcs',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.flexVentas__gcs'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number(row.gcs ?? 0),
  });
  handler.addColumn({
    name: 'flexVentas__total',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.flexVentas__amt'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number(row.totalAmt ?? 0),
  });

  // ---------------- Fiscal (detail: GCs / Total Amount)
  handler.addColumn({
    name: 'fiscal__gcs',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.fiscal__gcs'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number((row as any).gcsFs ?? (row as any).gcsFiscal ?? 0),
  });
  handler.addColumn({
    name: 'fiscal__total',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.fiscal__amt'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number((row as any).totalAmtFs ?? (row as any).totalAmtFiscal ?? 0),
  });

  // ---------------- Cancelled by Substitution (detail: GCs / Total Amount)
  handler.addColumn({
    name: 'cancelledBySubstitution__gcs',
    type: 'number',
    title: ({ t }) =>
      // usa la key que prefieras en tu i18n
      t('conciliationHeaders.columns.cancelledBySubstitution__gcs'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number(row.qtyCancSust ?? 0),
  });
  handler.addColumn({
    name: 'cancelledBySubstitution__total',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.cancelledBySubstitution__amt'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number(row.totalAmtCancSust ?? 0),
  });

  // ---------------- Not Conciliated (detail: GCs / Total Amount)
  handler.addColumn({
    name: 'notConciliated__gcs',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.notConciliated__gcs'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number(row.gcsDif ?? 0),
  });
  handler.addColumn({
    name: 'notConciliated__total',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.notConciliated__amt'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number(row.totalAmtDif ?? 0),
  });

  // ---------------- Registered only in Flex Ventas (detail: GCs / Total Amount)
  handler.addColumn({
    name: 'onlyFlexVentas__gcs',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.registeredOnlyInFlexVentas__gcs'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number(row.gcsFv ?? 0),
  });
  handler.addColumn({
    name: 'onlyFlexVentas__total',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.registeredOnlyInFlexVentas__amt'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number(row.totalAmtFv ?? 0),
  });

  // ---------------- Registered only in Fiscal (detail: GCs / Total Amount)
  handler.addColumn({
    name: 'onlyFiscal__gcs',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.registeredOnlyInFiscal__gcs'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number(row.gcsFiscal ?? 0),
  });
  handler.addColumn({
    name: 'onlyFiscal__total',
    type: 'number',
    title: ({ t }) => t('conciliationHeaders.columns.registeredOnlyInFiscal__amt'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number(row.totalAmtFiscal ?? 0),
  });

  handler.setExportColumnPredicate((col, ctx) => {
    console.log('countryCode en predicate: ', ctx.country);
    if (ctx.conciliationMethod === 'G') return PA_COLUMNS.includes(col.id as any);
    return (
      ['store', 'federativeEntity', 'accountingDate', 'status'].includes(col.id) ||
      col.id.includes('__')
    ); // agregue 'federativeEntity',
  });
  return handler;
};
